/**
 * Connector catalog read governance returns fail-fast `library_busy_scanning`
 * while scans or write pressure hold the DB. get_albums may also hit the 20s
 * command timeout during the same conditions. UI keeps warm wall paint and backs off.
 */

import { applyLibraryRebuildFromError } from './libraryRebuilding';

export type LibraryBusyScanningError = {
  code: 'library_busy_scanning';
  scan_active?: boolean;
  message?: string;
};

const BUSY_CODE = 'library_busy_scanning';

/** Scan-aware backoff: 2s → 4s → 8s → … cap 30s (#366). */
export const LIBRARY_CATALOG_RETRY_BASE_MS = 2000;
export const LIBRARY_CATALOG_RETRY_CAP_MS = 30_000;

export const LIBRARY_BUSY_RETRY_BACKOFF_MS = [
  2000,
  4000,
  8000,
  16_000,
  30_000,
] as const;

export const LIBRARY_BUSY_MAX_ATTEMPTS = 24;

function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error ?? '');
  }
}

/** Parse structured busy payload from IPC/HTTP error text. */
export function parseLibraryBusyScanningError(error: unknown): LibraryBusyScanningError | null {
  const text = errorText(error).trim();
  if (!text.includes(BUSY_CODE)) return null;

  const jsonStart = text.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(text.slice(jsonStart)) as Record<string, unknown>;
      if (String(parsed.code ?? '') === BUSY_CODE) {
        return {
          code: BUSY_CODE,
          scan_active: parsed.scan_active === true || parsed.scan_active === 'true',
          message: typeof parsed.message === 'string' ? parsed.message : undefined,
        };
      }
    } catch {
      /* fall through */
    }
  }

  const scanActive = /"scan_active"\s*:\s*true/.test(text) || /\bscan_active=true\b/.test(text);
  return { code: BUSY_CODE, scan_active: scanActive };
}

export function isLibraryBusyScanningError(error: unknown): boolean {
  return parseLibraryBusyScanningError(error) != null;
}

/** Connector get_albums command timeout (20s backend + client wrapper). */
export function isGetAlbumsTimeoutError(error: unknown): boolean {
  const text = errorText(error).toLowerCase();
  return text.includes('get_albums timed out')
    || (text.includes('timed out after') && text.includes('get_albums'));
}

export function isLibraryCatalogRetryableError(error: unknown): boolean {
  return isLibraryBusyScanningError(error) || isGetAlbumsTimeoutError(error);
}

/** Exponential delay for scheduled UI retries (attempt 0 → 2s, 1 → 4s, … cap 30s). */
export function nextLibraryCatalogRetryDelayMs(attempt: number): number {
  const exp = LIBRARY_CATALOG_RETRY_BASE_MS * (2 ** Math.max(0, attempt));
  return Math.min(exp, LIBRARY_CATALOG_RETRY_CAP_MS);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

/** Retry on library_busy_scanning and get_albums timeout; rethrow other errors immediately. */
export async function callWithLibraryBusyRetry<T>(
  fn: () => Promise<T>,
  options: {
    signal?: AbortSignal;
    maxAttempts?: number;
    onBusy?: (attempt: number, detail: LibraryBusyScanningError) => void;
    onRetryable?: (attempt: number, error: unknown) => void;
  } = {},
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? LIBRARY_BUSY_MAX_ATTEMPTS;
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      applyLibraryRebuildFromError(error);
      const busy = parseLibraryBusyScanningError(error);
      const retryable = busy || isGetAlbumsTimeoutError(error);
      if (!retryable) throw error;

      if (busy) {
        options.onBusy?.(attempt, busy);
      } else {
        options.onRetryable?.(attempt, error);
      }

      if (attempt >= maxAttempts - 1) break;

      const delay = nextLibraryCatalogRetryDelayMs(attempt);
      await sleep(delay, options.signal);
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error(String(lastError ?? BUSY_CODE));
}
